import React from 'react';
import { Task } from '../../../types';
import { GuidedWelcome } from './GuidedWelcome';
import { GuidedTaskBrief } from './GuidedTaskBrief';
import { GuidedRest } from './GuidedRest';
import { GuidedSessionComplete } from './GuidedSessionComplete';
import './Guided.css';

export type GuidedStep = 'welcome' | 'brief' | 'rest' | 'complete';

export interface GuidedHomeShellProps {
  step: GuidedStep;
  firstName: string;
  done: number;
  total: number;
  /** The next-incomplete task (or null if none remain). Used by welcome
      to render the preview chip and by rest to advance to it. */
  nextTask: Task | null;
  /** Researcher's intro message — Project.welcomeMessage. */
  researcherQuote?: string;
  /** Researcher's closing message — Project.closingMessage (not yet
      an admin field; falls back to a default inside the screen). */
  closingMessage?: string;

  /** Welcome's "Begin/Continue" CTA. Caller should transition to the
      brief for `nextTask`. */
  onBegin: () => void;
  /** Rest screen tapped / auto-advanced. Caller should transition to
      the brief for `nextTask`. */
  onAdvance: () => void;
  /** Brief's "Start task" CTA. Caller opens the capture screen for
      `nextTask`. */
  onStartTask: () => void;
  /** Brief's back-arrow. Returns the participant to the previous step
      (welcome if 0 done, rest otherwise). */
  onBackFromBrief: () => void;
}

/** Thin orchestrator — selects the right ceremony screen for the
    given step. The state machine itself lives one level up in
    ParticipantNewPage (it owns page.screen / setPage). This component
    is the visual mux + glue between guided screens. */
export const GuidedHomeShell: React.FC<GuidedHomeShellProps> = ({
  step,
  firstName,
  done,
  total,
  nextTask,
  researcherQuote,
  closingMessage,
  onBegin,
  onAdvance,
  onStartTask,
  onBackFromBrief,
}) => {
  if (step === 'brief' && nextTask) {
    return (
      <GuidedTaskBrief
        task={nextTask}
        done={done}
        total={total}
        onStart={onStartTask}
        onBack={onBackFromBrief}
      />
    );
  }
  if (step === 'rest') {
    return <GuidedRest done={done} total={total} onAdvance={onAdvance} />;
  }
  if (step === 'complete') {
    return (
      <GuidedSessionComplete
        firstName={firstName}
        total={total}
        closingMessage={closingMessage}
      />
    );
  }
  // welcome (default / first-visit / return)
  return (
    <GuidedWelcome
      firstName={firstName}
      done={done}
      total={total}
      nextTask={nextTask}
      researcherQuote={researcherQuote}
      onBegin={onBegin}
    />
  );
};
